import { useEffect } from "react";
import { useImmer } from "use-immer";
import { errorMessage } from "../../../utils";
import { getStampUsageHistoryApi } from "../api";
import qs from "qs";
import { useParams } from "react-router-dom";
import moment from "moment";
import { useDebouncedCallback } from "use-debounce";

export const useStampUsageHistory = () => {
  const { id } = useParams();

  const [state, setState] = useImmer({
    isLoading: true,
    dataList: [],
    pagination: {
      pageIndex: 1,
      pageSize: 50,
      pageCount: 0,
      total: 0,
      hasMorePages: true,
      lastPage: 1,
    },
    filterApplied: {
      search: "",
      order_date: null,
      all: [],
    },
    disableApplyButton: false,
  });

  const getStampUsageHistory = async ({
    pagination = state.pagination,
    search = state.filterApplied.search,
    order_date = state.filterApplied.order_date,
  } = {}) => {
    setState((draft) => {
      draft.isLoading = true;
    });

    try {
      let params = {};

      if (search) {
        params.search = search;
      }

      if (order_date && order_date.value) {
        params.order_date = moment(order_date.value).format("YYYY-MM-DD");
      }

      const query = {
        params,
        paramsSerializer: (params) =>
          qs.stringify(params, {
            encode: true,
            arrayFormat: "brackets",
            encodeValuesOnly: true,
          }),
      };

      const res = await getStampUsageHistoryApi(id, query, pagination);
      console.log(res);

      if (res?.success) {
        setState((draft) => {
          draft.dataList = res.data.transactions || [];
          draft.pagination = res.data.pagination;
          if (search !== undefined) draft.filterApplied.search = search;
          if (order_date !== undefined)
            draft.filterApplied.order_date = order_date;

          draft.filterApplied.all = [draft.filterApplied.order_date].filter(
            (el) => el && Object.keys(el).length,
          );

          draft.isLoading = false;
        });
      }
    } catch (err) {
      console.log(err);
      errorMessage(err?.response?.data?.message || "Something went wrong");
      setState((draft) => {
        draft.isLoading = false;
      });
    }
  };

  useEffect(() => {
    if (id) {
      getStampUsageHistory();
    }
  }, [id]);

  const debouncedSearch = useDebouncedCallback((value) => {
    const { order_date } = state.filterApplied;
    const { pagination } = state;

    getStampUsageHistory({
      search: value,
      order_date,
      pagination: {
        pageIndex: 1,
        pageSize: pagination.pageSize,
      },
    });
  }, 500);

  const handleSearch = (value) => {
    setState((draft) => {
      draft.filterApplied.search = value;
    });
    debouncedSearch(value);
  };

  const onChangeDateFilter = (date) => {
    setState((draft) => {
      draft.filterApplied.order_date = date
        ? {
            value: date,
            label: moment(date).format("DD-MM-YYYY"),
            type: "Order Date",
          }
        : null;
      draft.disableApplyButton = false;
    });
  };

  const handleApplyFilter = () => {
    const { search, order_date } = state.filterApplied;
    const { pagination } = state;

    getStampUsageHistory({
      search,
      order_date,
      pagination: {
        pageIndex: 1,
        pageSize: pagination.pageSize,
      },
    });
  };

  const handleFilterClear = (type, filter) => {
    if (type === "single") {
      if (filter.type === "Order Date") {
        setState((draft) => {
          draft.filterApplied.order_date = null;
        });
      }

      setState((draft) => {
        draft.disableApplyButton = false;
      });
    } else {
      setState((draft) => {
        draft.filterApplied = {
          search: draft.filterApplied.search,
          order_date: null,
          all: [],
        };
        draft.isLoading = true;
      });

      getStampUsageHistory({
        search: state.filterApplied.search,
        pagination: {
          pageIndex: 1,
          pageSize: state.pagination.pageSize,
        },
        order_date: null,
      });
    }
  };

  const gotoPage = (page) => {
    const { search, order_date } = state.filterApplied;

    getStampUsageHistory({
      search,
      order_date,
      pagination: {
        pageIndex: page,
        pageSize: state.pagination.pageSize,
      },
    });

    setState((draft) => {
      draft.pagination.pageIndex = page;
    });
  };

  const setPageSize = (e) => {
    const { search, order_date } = state.filterApplied;

    getStampUsageHistory({
      search,
      order_date,
      pagination: {
        pageIndex: 1,
        pageSize: +e.target.value,
      },
    });

    setState((draft) => {
      draft.pagination.pageSize = +e.target.value;
    });
  };

  useEffect(() => {
    setState((draft) => {
      draft.filterApplied.all = [state.filterApplied.order_date].filter(
        (el) => el && Object.keys(el).length,
      );
    });
  }, [state.filterApplied.order_date]);

  return {
    state,
    handleSearch,
    onChangeDateFilter,
    handleFilterClear,
    handleApplyFilter,
    gotoPage,
    setPageSize,
  };
};
